import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock3, Headset, Mail, MapPin, MessageSquare, Send } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/paisa/ui";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TOPICS = [
  { label: "Account & verification", category: "kyc" },
  { label: "P2P trade or dispute", category: "trading" },
  { label: "OTC desk", category: "trading" },
  { label: "Deposits & withdrawals", category: "withdrawal" },
  { label: "Login & security", category: "login" },
  { label: "Partnerships", category: "other" },
  { label: "Other", category: "other" },
];
const PRIORITIES = ["low", "medium", "high", "critical"];

const CHANNELS = [
  { icon: Headset, title: "24/7 live support", body: "Average first response under 5 minutes.", value: "support@paisacx.io", href: "mailto:support@paisacx.io" },
  { icon: MessageSquare, title: "OTC desk", body: "Block trades from ₹10 L, Mon–Sat.", value: "otc@paisacx.io", href: "mailto:otc@paisacx.io" },
  { icon: Mail, title: "Partnerships & press", body: "Listings, integrations and media.", value: "partners@paisacx.io", href: "mailto:partners@paisacx.io" },
];

const STATUS_TONE = {
  open: "text-primary",
  in_progress: "text-warning",
  resolved: "text-up",
  closed: "text-muted-foreground",
};

const DRAFT_KEY = "paisacx.contact.draft";
const EMPTY = { name: "", email: "", topic: TOPICS[0].label, priority: "medium", orderId: "", message: "" };

const field =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none transition placeholder:text-muted-foreground/70 focus:border-primary";

function validate(f) {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Enter your name";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Enter a valid email";
  if (f.message.trim().length < 20) e.message = "Tell us a little more (at least 20 characters)";
  return e;
}

function readDraft() {
  try {
    return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
  } catch {
    return null;
  }
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-down">{error}</span>}
    </label>
  );
}

export default function SupportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(readDraft() || {}) }));
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f, name: f.name || user.full_name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/api/support/tickets");
      setTickets(data || []);
      return data || [];
    } catch {
      setTickets((t) => t || []);
      return null;
    }
  };

  useEffect(() => {
    if (user) loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!user) {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      navigate("/login", { state: { from: "/contact" } });
      return;
    }
    setSending(true);
    try {
      const topic = TOPICS.find((t) => t.label === form.topic) || TOPICS[TOPICS.length - 1];
      const contactLine = form.email !== user.email || form.name !== (user.full_name || user.name) ? `\n\n— ${form.name} <${form.email}>` : "";
      const { data } = await api.post("/api/support/tickets", {
        subject: `${topic.label}${form.orderId.trim() ? ` · ${form.orderId.trim()}` : ""}`,
        body: `${form.message.trim()}${contactLine}`,
        priority: form.priority,
        category: topic.category,
      });
      sessionStorage.removeItem(DRAFT_KEY);
      setTicket(data?.id || "—");
      loadTickets();
    } catch (err) {
      setErrors({ message: getErrorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell requireAuth={false} bare>
      <div className="page-fade">
        <section className="hero-grid border-b border-border" data-testid="support-hero">
          <div className="mx-auto max-w-7xl px-4 py-12 text-center sm:py-16">
            <h1 className="text-4xl font-semibold sm:text-5xl">
              Contact <span className="brand-text">PaisaCX</span>
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-6 text-muted-foreground sm:text-base sm:leading-7">
              Questions about an order, your account or a large trade? Our team is here around the clock.
            </p>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-6 px-4 py-12 lg:grid-cols-[1fr_1.4fr]">
          <div className="space-y-4">
            {CHANNELS.map(({ icon: Icon, title, body, value, href }) => (
              <a
                key={title}
                href={href}
                className="flex gap-4 rounded-xl border border-border bg-surface p-5 transition hover:-translate-y-0.5 hover:border-primary/50"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-semibold">{title}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">{body}</span>
                  <span className="mt-2 block text-sm font-medium text-primary">{value}</span>
                </span>
              </a>
            ))}
            <div className="grid gap-4 rounded-xl border border-border bg-surface p-5 sm:grid-cols-2 lg:grid-cols-1">
              <div className="flex gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-semibold">Head office</p>
                  <p className="mt-1 text-sm text-muted-foreground">Level 12, One BKC, Bandra Kurla Complex, Mumbai 400051, India</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-semibold">Hours</p>
                  <p className="mt-1 text-sm text-muted-foreground">Support: 24/7 · OTC desk: Mon–Sat, 9:00–21:00 IST</p>
                </div>
              </div>
            </div>
          </div>

          <div className="shadow-panel rounded-2xl border border-border bg-surface p-5 sm:p-8">
            {ticket ? (
              <div className="page-fade flex h-full flex-col items-center justify-center py-10 text-center">
                <CheckCircle2 className="h-12 w-12 text-primary" />
                <h2 className="mt-4 text-2xl font-semibold">Message received</h2>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                  We&apos;ve opened ticket <span className="num font-semibold text-foreground">#{String(ticket).slice(-6).toUpperCase()}</span> and will
                  reply here and at <span className="text-foreground">{form.email}</span> shortly.
                </p>
                <Button
                  variant="outline"
                  className="mt-6 border-primary/40"
                  onClick={() => {
                    setTicket(null);
                    setForm((f) => ({ ...EMPTY, name: f.name, email: f.email }));
                  }}
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="space-y-4" data-testid="create-ticket-modal">
                <div>
                  <h2 className="text-2xl font-semibold">Send us a message</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {user ? "We usually reply within a few hours." : "You'll be asked to log in so we can link the ticket to your account."}
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name" error={errors.name}>
                    <input value={form.name} onChange={set("name")} placeholder="Aarav Sharma" className={field} />
                  </Field>
                  <Field label="Email" error={errors.email}>
                    <input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={field} />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-[1.4fr_1fr_1fr]">
                  <Field label="Topic">
                    <select value={form.topic} onChange={set("topic")} className={field} data-testid="ticket-subject">
                      {TOPICS.map((t) => (
                        <option key={t.label}>{t.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Priority">
                    <select value={form.priority} onChange={set("priority")} className={`${field} capitalize`} data-testid="ticket-priority">
                      {PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {p[0].toUpperCase() + p.slice(1)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Order ID (optional)">
                    <input value={form.orderId} onChange={set("orderId")} placeholder="e.g. P2P-48213" className={`${field} num`} />
                  </Field>
                </div>
                <Field label="Message" error={errors.message}>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    rows={6}
                    placeholder="Describe your question or issue…"
                    className={`${field} resize-y`}
                    data-testid="ticket-body"
                  />
                </Field>
                <Button type="submit" disabled={sending} className="brand-gradient shadow-brand h-11 w-full px-6 sm:w-auto" data-testid="ticket-submit">
                  {sending ? "Sending…" : user ? "Send message" : "Log in and send"} <Send />
                </Button>
                <p className="text-xs text-muted-foreground">PaisaCX staff will never ask for your password, 2FA codes or seed phrase.</p>
              </form>
            )}
          </div>
        </section>

        {user && (
          <section className="mx-auto max-w-7xl px-4 pb-14">
            <h2 className="text-xl font-semibold">Your tickets</h2>
            <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-surface/40" data-testid="my-tickets">
              <div className="min-w-[700px]">
                <div className="table-head grid grid-cols-[100px_1.6fr_100px_110px_120px_80px] gap-3 px-4 py-2.5">
                  <span>Ticket</span>
                  <span>Subject</span>
                  <span>Priority</span>
                  <span>Status</span>
                  <span className="text-right">Updated</span>
                  <span />
                </div>
                {tickets === null ? (
                  <div className="space-y-2 p-4">
                    {[0, 1].map((i) => (
                      <div key={i} className="skeleton h-10 w-full" />
                    ))}
                  </div>
                ) : tickets.length === 0 ? (
                  <p className="px-4 py-12 text-center text-sm text-muted-foreground">No tickets yet. Messages you send above appear here.</p>
                ) : (
                  tickets.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setOpen(t)}
                      data-testid={`my-ticket-${t.id}`}
                      className="terminal-row w-full grid-cols-[100px_1.6fr_100px_110px_120px_80px] gap-3 px-4 py-3 text-left text-sm"
                    >
                      <span className="num text-muted-foreground">#{String(t.id).slice(-6).toUpperCase()}</span>
                      <span className="truncate">{t.subject}</span>
                      <span className="text-xs capitalize text-muted-foreground">{t.priority}</span>
                      <span className={`text-xs capitalize ${STATUS_TONE[t.status] || "text-muted-foreground"}`}>{String(t.status).replace("_", " ")}</span>
                      <span className="num text-right text-xs text-muted-foreground">
                        {new Date(t.updated_at || t.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                      </span>
                      <span className="text-right text-xs text-primary">View</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </section>
        )}
      </div>

      {open && (
        <TicketDrawer
          ticket={open}
          onClose={() => setOpen(null)}
          onReplied={async () => {
            const list = await loadTickets();
            if (list) setOpen(list.find((x) => x.id === open.id) || null);
          }}
        />
      )}
    </AppShell>
  );
}

function TicketDrawer({ ticket, onClose, onReplied }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const canReply = ticket.status !== "closed" && ticket.status !== "resolved";

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(`/api/support/tickets/${ticket.id}/reply`, { body: reply.trim() });
      setReply("");
      await onReplied();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={ticket.subject}
      subtitle={`#${String(ticket.id).slice(-6).toUpperCase()} · ${ticket.priority} priority · ${String(ticket.status).replace("_", " ")}`}
      onClose={onClose}
      testId="ticket-drawer"
      width="max-w-xl"
      footer={
        canReply ? (
          <div className="space-y-2">
            <textarea
              rows={3}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write a reply…"
              data-testid="user-reply"
              className={`${field} resize-none`}
            />
            {err && <p className="text-xs text-down">{err}</p>}
            <button
              type="button"
              onClick={send}
              disabled={busy || !reply.trim()}
              className="brand-gradient rounded-lg px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40"
            >
              {busy ? "Sending…" : "Reply"}
            </button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">This ticket is {ticket.status}. Send a new message if you need more help.</p>
        )
      }
    >
      <div className="space-y-3">
        {(ticket.messages || []).map((m, i) => {
          const staff = m.from === "admin";
          return (
            <div key={i} className={`flex ${staff ? "justify-start" : "justify-end"}`}>
              <div
                className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${
                  staff ? "border border-border bg-surface-2" : "bg-primary/[0.12] text-foreground"
                }`}
              >
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {staff ? "PaisaCX support" : "You"} · {new Date(m.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Drawer>
  );
}
